import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Flag, Play, Square, RefreshCw, Plus } from 'lucide-react';
import {
  type Goal,
  type GoalRound,
  createGoal,
  getGoal,
  listGoalRounds,
  listGoals,
  resumeGoal,
  stopGoal,
} from '../api/goals';
import { streamJobEvents } from '../api/sse';
import EvalSynthesizePanel from '../components/EvalSynthesizePanel';

/** 目标状态中文文案与徽章样式。 */
const STATUS_META: Record<Goal['status'], { label: string; badge: string }> = {
  active: { label: '进行中', badge: 'info' },
  completed: { label: '已达标', badge: 'success' },
  blocked: { label: '已阻塞', badge: 'warning' },
  failed: { label: '未达标（达到轮次上限）', badge: 'error' },
  budget_exhausted: { label: '预算耗尽', badge: 'warning' },
};

const VERDICT_META: Record<GoalRound['verdict'], { label: string; badge: string }> = {
  pass: { label: '达标', badge: 'success' },
  fail: { label: '未达标', badge: 'info' },
  blocked: { label: '阻塞', badge: 'warning' },
};

interface CreateForm {
  objective: string;
  criteriaType: 'llm' | 'benchmark';
  criteriaText: string;
  artifactId: string;
  suiteId: string;
  targetScore: string;
  maxRounds: string;
  budgetTokens: string;
}

const EMPTY_FORM: CreateForm = {
  objective: '',
  criteriaType: 'llm',
  criteriaText: '',
  artifactId: '',
  suiteId: '',
  targetScore: '0.9',
  maxRounds: '20',
  budgetTokens: '',
};

/**
 * 目标驱动验证（P9 目标模式）：声明验收目标，系统循环驱动直到
 * 达标 / 诚实报阻塞（同一阻塞连续 3 轮）/ 预算耗尽（收尾轮给结论）/ 轮次硬上限。
 * 进度经 JobEvent 游标重放（刷新页面不丢进度）。
 */
export default function GoalCenter() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  // B8：从「合成评测用例 → 创建目标验证」跳转进入时（?suiteId=），
  // 初始化即预填 benchmark 表单并展开，便于直接绑定刚转好的评测套件
  const [showForm, setShowForm] = useState(() => Boolean(searchParams.get('suiteId')));
  const [form, setForm] = useState<CreateForm>(() => {
    const suiteId = searchParams.get('suiteId');
    return suiteId ? { ...EMPTY_FORM, criteriaType: 'benchmark', suiteId } : EMPTY_FORM;
  });
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Goal | null>(null);
  const [rounds, setRounds] = useState<GoalRound[]>([]);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const refreshGoals = useCallback(async () => {
    if (!agentId) return;
    try {
      setGoals(await listGoals(agentId));
    } catch (e) {
      console.error('加载目标列表失败:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  const refreshDetail = useCallback(async (goalId: string) => {
    const [g, r] = await Promise.all([getGoal(goalId), listGoalRounds(goalId)]);
    setDetail(g);
    setRounds(r);
    return g;
  }, []);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState（react-hooks/set-state-in-effect）
    const tick = () => {
      void refreshGoals();
    };
    tick();
  }, [refreshGoals]);

  /** B8：合成套件 materialize 后预填目标表单（无需跳转，本页即创建入口）。 */
  const handleSuiteMaterialized = (suiteId: string) => {
    setForm({ ...EMPTY_FORM, criteriaType: 'benchmark', suiteId });
    setShowForm(true);
  };

  // 选中目标：拉详情 + 轮次；进行中且有任务时接 JobEvent 流（历史重建 + 实时跟随）
  useEffect(() => {
    if (!selectedId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const g = await refreshDetail(selectedId);
        if (g.status !== 'active' || !g.job_id) return;
        await streamJobEvents(
          g.job_id,
          (event) => {
            // round / stopped / done 任一事件都说明状态有推进，刷新详情（幂等）
            if (event.type === 'round' || event.type === 'done' || event.type === 'stopped') {
              refreshDetail(selectedId).catch((e) => console.error('刷新目标详情失败:', e));
              refreshGoals();
            }
          },
          controller.signal
        );
      } catch (e) {
        if (!controller.signal.aborted) console.error('订阅目标进度失败:', e);
      }
    })();

    return () => controller.abort();
  }, [selectedId, refreshDetail, refreshGoals]);

  const handleCreate = async () => {
    if (!agentId) return;
    setError('');
    if (!form.objective.trim()) {
      setError('请填写目标描述');
      return;
    }
    let success_criteria: Goal['success_criteria'];
    if (form.criteriaType === 'benchmark') {
      const target = Number(form.targetScore);
      if (!form.artifactId.trim() || !form.suiteId.trim() || !(target > 0 && target <= 1)) {
        setError('绑定评测需填写产物 ID、套件 ID 与 (0, 1] 区间内的目标分数');
        return;
      }
      success_criteria = {
        type: 'benchmark',
        artifact_id: form.artifactId.trim(),
        suite_id: form.suiteId.trim(),
        target_score: target,
      };
    } else {
      if (!form.criteriaText.trim()) {
        setError('请填写验收标准');
        return;
      }
      success_criteria = { type: 'llm', criteria: form.criteriaText.trim() };
    }

    setCreating(true);
    try {
      const { goal } = await createGoal(agentId, {
        objective: form.objective.trim(),
        success_criteria,
        max_rounds: Math.max(1, Number(form.maxRounds) || 20),
        budget_tokens: form.budgetTokens.trim() ? Math.max(1, Number(form.budgetTokens)) : null,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await refreshGoals();
      setSelectedId(goal.id);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || '创建目标失败');
    } finally {
      setCreating(false);
    }
  };

  const handleStop = async (goalId: string) => {
    try {
      await stopGoal(goalId);
      await refreshDetail(goalId);
      await refreshGoals();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || '停止失败');
    }
  };

  const handleResume = async (goalId: string) => {
    setError('');
    try {
      await resumeGoal(goalId);
      await refreshDetail(goalId);
      await refreshGoals();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || '恢复失败');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1>目标驱动验证</h1>
          <p className="text-muted" style={{ margin: '4px 0 0' }}>
            声明验收目标，系统循环驱动直到达标 / 诚实报阻塞（同一阻塞连续 3 轮）/ 预算耗尽 / 轮次上限
          </p>
        </div>
        <button className="primary" onClick={() => setShowForm((v) => !v)}>
          <Plus size={14} /> 新建目标
        </button>
      </div>

      {error && (
        <div className="card mb-4" style={{ borderColor: 'var(--color-error, #dc2626)' }}>
          <p style={{ margin: 0, color: 'var(--color-error, #dc2626)' }}>{error}</p>
        </div>
      )}

      <div className="mb-4">
        <EvalSynthesizePanel agentId={agentId || ''} onMaterialized={handleSuiteMaterialized} />
      </div>

      {showForm && (
        <div className="card mb-4" style={{ background: 'var(--bg-primary)' }}>
          <h3 style={{ marginTop: 0 }}>新建验收目标</h3>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <label style={{ width: 110, fontSize: 13 }} className="text-muted">目标描述</label>
              <input
                type="text"
                style={{ flex: 1 }}
                value={form.objective}
                placeholder="如：SOP 问答正确率 ≥90% / 所有测试通过"
                onChange={(e) => setForm({ ...form, objective: e.target.value })}
              />
            </div>
            <div className="flex gap-2 items-center">
              <label style={{ width: 110, fontSize: 13 }} className="text-muted">验收方式</label>
              <select
                value={form.criteriaType}
                onChange={(e) => setForm({ ...form, criteriaType: e.target.value as CreateForm['criteriaType'] })}
              >
                <option value="llm">自由文本（由 LLM 判定）</option>
                <option value="benchmark">绑定评测套件（跑分判定）</option>
              </select>
            </div>
            {form.criteriaType === 'llm' ? (
              <div className="flex gap-2 items-start">
                <label style={{ width: 110, fontSize: 13, paddingTop: 8 }} className="text-muted">验收标准</label>
                <textarea
                  rows={3}
                  style={{ flex: 1 }}
                  value={form.criteriaText}
                  placeholder="如：抽样 20 道 SOP 问题，回答正确率不低于 90%"
                  onChange={(e) => setForm({ ...form, criteriaText: e.target.value })}
                />
              </div>
            ) : (
              <>
                <div className="flex gap-2 items-center">
                  <label style={{ width: 110, fontSize: 13 }} className="text-muted">评测产物 ID</label>
                  <input
                    type="text"
                    style={{ flex: 1 }}
                    value={form.artifactId}
                    placeholder="Artifact UUID"
                    onChange={(e) => setForm({ ...form, artifactId: e.target.value })}
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <label style={{ width: 110, fontSize: 13 }} className="text-muted">评测套件 ID</label>
                  <input
                    type="text"
                    style={{ flex: 1 }}
                    value={form.suiteId}
                    placeholder="如 sop-qa-basic"
                    onChange={(e) => setForm({ ...form, suiteId: e.target.value })}
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <label style={{ width: 110, fontSize: 13 }} className="text-muted">目标综合分</label>
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={form.targetScore}
                    onChange={(e) => setForm({ ...form, targetScore: e.target.value })}
                  />
                  <span className="text-muted" style={{ fontSize: 12 }}>(0, 1]，综合分达到即判达标</span>
                </div>
              </>
            )}
            <div className="flex gap-2 items-center">
              <label style={{ width: 110, fontSize: 13 }} className="text-muted">最大轮次</label>
              <input
                type="number"
                min="1"
                max="200"
                value={form.maxRounds}
                onChange={(e) => setForm({ ...form, maxRounds: e.target.value })}
              />
              <span className="text-muted" style={{ fontSize: 12 }}>硬上限兜底，防止无限循环</span>
            </div>
            <div className="flex gap-2 items-center">
              <label style={{ width: 110, fontSize: 13 }} className="text-muted">token 预算</label>
              <input
                type="number"
                min="1"
                value={form.budgetTokens}
                placeholder="留空不限"
                onChange={(e) => setForm({ ...form, budgetTokens: e.target.value })}
              />
              <span className="text-muted" style={{ fontSize: 12 }}>耗尽前注入收尾轮给出结论</span>
            </div>
            <div>
              <button className="primary" disabled={creating} onClick={handleCreate}>
                <Play size={14} /> {creating ? '创建中…' : '创建并开始驱动'}
              </button>
            </div>
          </div>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="empty">
          <Flag size={32} />
          <h3>暂无目标</h3>
          <p className="text-muted">点击「新建目标」声明验收标准，系统将自动循环驱动验证</p>
        </div>
      ) : (
        <div className="list mb-4">
          {goals.map((goal) => {
            const meta = STATUS_META[goal.status] || STATUS_META.active;
            return (
              <div
                key={goal.id}
                className="list-item"
                style={{
                  cursor: 'pointer',
                  borderLeft: selectedId === goal.id ? '3px solid var(--color-primary, #4f46e5)' : undefined,
                }}
                onClick={() => setSelectedId(goal.id)}
              >
                <div className="list-item-content">
                  <div className="flex items-center gap-2">
                    <h3 style={{ margin: 0 }}>{goal.objective}</h3>
                    <span className={`badge ${meta.badge}`}>{meta.label}</span>
                  </div>
                  <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                    第 {goal.rounds}/{goal.max_rounds} 轮
                    {goal.budget_tokens ? ` · token ${goal.used_tokens}/${goal.budget_tokens}` : ''}
                    {goal.blocker_reason ? ` · ${goal.blocker_reason.slice(0, 60)}` : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detail && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 style={{ margin: 0 }}>{detail.objective}</h3>
              <span className={`badge ${STATUS_META[detail.status].badge}`}>{STATUS_META[detail.status].label}</span>
            </div>
            <div className="flex gap-2">
              {detail.status === 'active' && (
                <>
                  <button onClick={() => handleStop(detail.id)}>
                    <Square size={14} /> 停止
                  </button>
                  <button onClick={() => handleResume(detail.id)}>
                    <RefreshCw size={14} /> 恢复驱动
                  </button>
                </>
              )}
            </div>
          </div>
          <p className="text-muted text-sm">
            进度：第 {detail.rounds}/{detail.max_rounds} 轮
            {detail.budget_tokens ? ` · token 已用 ${detail.used_tokens}/${detail.budget_tokens}` : ''}
            {detail.consecutive_blockers > 0 ? ` · 同一阻塞已连续 ${detail.consecutive_blockers}/3 轮` : ''}
          </p>

          {/* 终态展示：达标报告 / 阻塞原因 / 收尾结论 */}
          {detail.status === 'completed' && (
            <div className="card mt-2" style={{ borderColor: 'var(--color-success, #16a34a)' }}>
              <strong>达标报告：</strong>目标在第 {detail.rounds} 轮达成验收标准。
            </div>
          )}
          {(detail.status === 'blocked' || detail.status === 'budget_exhausted' || detail.status === 'failed') &&
            detail.blocker_reason && (
              <div className="card mt-2" style={{ borderColor: 'var(--color-warning, #d97706)' }}>
                <strong>
                  {detail.status === 'blocked' ? '阻塞原因：' : detail.status === 'failed' ? '终结原因：' : '收尾结论：'}
                </strong>
                {detail.blocker_reason}
              </div>
            )}

          <h4 className="mt-4">轮次时间线</h4>
          {rounds.length === 0 ? (
            <p className="text-muted text-sm">尚无轮次记录</p>
          ) : (
            <div className="list">
              {rounds.map((r) => (
                <div key={r.id} className="list-item">
                  <div className="list-item-content">
                    <div className="flex items-center gap-2">
                      <span>
                        第 {r.round_no} 轮{r.round_type === 'wrap_up' ? '（收尾轮）' : ''}
                      </span>
                      <span className={`badge ${VERDICT_META[r.verdict].badge}`}>{VERDICT_META[r.verdict].label}</span>
                      {r.score !== null && <span className="text-muted text-sm">得分 {(r.score * 100).toFixed(1)}%</span>}
                    </div>
                    <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                      {r.action_summary}
                      {r.blocker ? ` · 阻塞：${r.blocker}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
